import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import { SubmitButton } from "@/components/Interface/Button/Button";
import { PasswordField } from "@/components/Interface/FormFields/PasswordField/PasswordField";
import { PasswordStrength } from "@/components/Interface/PasswordStrength/PasswordStrength";
import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { useAlert } from "@/hooks/useAlert";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { getGetCustomerQueryKey, useGetCustomer } from "@/services/api";
import { useChangePassword } from "@/services/auth";
import { AlertTypes } from "@/utils/atoms";
import { createInitialPasswordSetupErrorHandler } from "@/utils/initialPasswordSetupErrorHandler";
import {
  getPasswordRequirementTokens,
  getPasswordValidationError,
} from "@/utils/passwordValidation";

type InitialPasswordSetupForm = {
  confirmPassword: string;
  password: string;
};

export const InitialPasswordSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showAlert = useAlert();
  const { data: customer, isLoading: isCustomerLoading } = useGetCustomer();
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);

  const {
    handleSubmit,
    register,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<InitialPasswordSetupForm>({
    defaultValues: {
      confirmPassword: "",
      password: "",
    },
    mode: "onChange",
  });

  const passwordRequirements = useMemo(
    () => getPasswordRequirementTokens(t),
    [t],
  );

  const handleSetPasswordError = useMemo(
    () => createInitialPasswordSetupErrorHandler(setError, t, showAlert),
    [setError, showAlert, t],
  );

  const { mutate: setPassword, isLoading: isSettingPassword } =
    useChangePassword({
      mutation: {
        onSuccess: () => {
          queryClient.setQueryData(
            getGetCustomerQueryKey(),
            (previousCustomer) =>
              previousCustomer
                ? {
                    ...previousCustomer,
                    initialPasswordChanged: true,
                  }
                : previousCustomer,
          );
          showAlert({
            type: AlertTypes.success,
            text: t(
              "user-account.personalDetails.loginData.passwordChangeSuccess",
            ),
          });
          navigate(portalRoutes.cockpit.base, { replace: true });
        },
        onError: handleSetPasswordError,
      },
    });

  const onSubmit = ({ password }: InitialPasswordSetupForm) => {
    setPassword({
      data: { newPassword: password },
    });
  };

  if (isCustomerLoading) {
    return <BrandLoadingScreen />;
  }

  if (customer?.initialPasswordChanged !== false) {
    return <Navigate to={portalRoutes.cockpit.base} replace />;
  }

  return (
    <>
      <h1 className="px-4 pb-6 pt-4 text-center text-headline-xl font-semibold leading-xs tracking-tight text-foreground-primary desktop:px-8">
        {t("translation:newWelcome.initialPasswordSetup.headline")}
      </h1>
      <div className="mx-auto max-w-2xl px-4 pb-8">
        <h2 className="mb-8 text-headline-md font-semibold text-foreground-primary">
          {t("translation:newWelcome.initialPasswordSetup.title")}
        </h2>

        {isSettingPassword && <LoadingSpinner />}

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6">
            <div>
              <PasswordField<InitialPasswordSetupForm>
                id="initial-password"
                name="password"
                label={t(
                  "translation:newWelcome.initialPasswordSetup.passwordLabel",
                )}
                register={register}
                errors={errors}
                options={{
                  required: {
                    value: true,
                    message: t(
                      "translation:Common.generalForm.validation.passwordRequired",
                    ),
                  },
                  validate: (value: string) =>
                    getPasswordValidationError(value, t),
                }}
                onFocus={() => setShowPasswordStrength(true)}
                onBlur={() => setShowPasswordStrength(false)}
              />
              {showPasswordStrength && (
                <div className="mt-2">
                  <PasswordStrength
                    password={watch("password")}
                    validationTokens={passwordRequirements}
                    inverted={false}
                  />
                </div>
              )}
            </div>

            <PasswordField<InitialPasswordSetupForm>
              id="confirm-initial-password"
              name="confirmPassword"
              label={t(
                "translation:newWelcome.initialPasswordSetup.confirmPasswordLabel",
              )}
              register={register}
              errors={errors}
              options={{
                required: {
                  value: true,
                  message: t(
                    "translation:newWelcome.registerForm.validation.confirmPasswordRequired",
                  ),
                },
                validate: (value: string) =>
                  value === watch("password") ||
                  t(
                    "translation:newWelcome.registerForm.validation.passwordNoMatch",
                  ),
              }}
              onFocus={() => setShowPasswordStrength(false)}
            />
          </div>

          <div className="mt-6 flex justify-center sm:mt-10">
            <SubmitButton
              className="accent capitalize"
              label={
                isSettingPassword ? (
                  <LoadingSpinner />
                ) : (
                  t("translation:newWelcome.initialPasswordSetup.submitButton")
                )
              }
              disabled={isSettingPassword || isSubmitting}
              fullWidth={false}
            />
          </div>
        </form>
      </div>
    </>
  );
};
